// Ably errors that the SDK recovers from on its own. Reporting them to Sentry
// only adds noise; see https://ably.com/docs/platform/errors/codes.
const transientAblyErrorCodes = new Set([
	40142, // Token expired: renewed automatically through authCallback
	80002, // Connection suspended
	80003, // Generic disconnection error
	80016, // Operation on superseded connection
	80017, // Connection closed (expected after client.close())
	90007, // Channel attach/detach timed out: retried while connected
]);

const transientAblyErrorMessages = new Set([
	'Connection to server unavailable',
	'Connection to server temporarily unavailable',
	'Channel attach timed out',
	'Channel detach timed out',
	'No error message',
]);

// authCallback failures that are network problems, not rejected credentials.
const recoverableAuthErrorMessages = [
	'ably token request timed out',
	'token request callback timed out',
	// ably-js 2.29+ wording for its own authCallback timeout
	'did not complete within the configured timeout',
	'timeout of',
	'network error',
	'canceled',
];

type AblyErrorLike = {
	code: number;
	statusCode?: number;
	message?: string;
	cause?: { message?: string } | null;
};

export function isAblyErrorLike(err: unknown): err is AblyErrorLike {
	return (
		typeof err === 'object' &&
		err !== null &&
		typeof (err as AblyErrorLike).code === 'number' &&
		'statusCode' in err
	);
}

function isRecoverableAuthError(err: AblyErrorLike) {
	if (err.code !== 40170 && !(err.code === 80019 && err.statusCode === 401)) {
		return false;
	}
	const message = `${err.message || ''} ${
		err.cause?.message || ''
	}`.toLowerCase();
	return recoverableAuthErrorMessages.some(value => message.includes(value));
}

export function isTransientAblyError(err: unknown): boolean {
	if (!isAblyErrorLike(err)) {
		return false;
	}
	return (
		transientAblyErrorCodes.has(err.code) ||
		(!!err.message && transientAblyErrorMessages.has(err.message)) ||
		isRecoverableAuthError(err)
	);
}

// Transport failures that reach the connection callbacks as plain errors (a
// dropped WebSocket has no message at all) or as the comet transport's own
// errors. Ably retries or switches transport on its own.
const ablyTransportErrorMessages = [
	'Transport superseded',
	'XHR error occurred',
	'Request aborted due to request timeout expiring',
	'Malformed response body from server',
];

export function isAblyTransportError(err: unknown): boolean {
	if (!(err instanceof Error)) {
		return false;
	}
	if (!err.message) {
		return !isAblyErrorLike(err);
	}
	return ablyTransportErrorMessages.some(value =>
		err.message.startsWith(value),
	);
}
